<script setup lang="ts">
  import { cloneDeep, isEqual } from 'lodash'
  import { useAuthStore } from '~/stores/useAuthStore'
  import type { IUpdateUserData } from './types'
  import { useDebounceFn } from '@vueuse/core'
  import { useFileStore } from '#imports'

  const emit = defineEmits<{
    (e: 'loading' | 'changed', value: boolean): void
  }>()

  defineProps<{
    isEditing: boolean
  }>()

  const { t } = useI18n()
  const store = useAuthStore()
  const fileStore = useFileStore()
  const { currentUser } = storeToRefs(store)
  const formRef = ref()
  const toast = useToast()

  const createInitialData = (): IUpdateUserData => ({
    contacts: (currentUser.value?.contacts || []).map((item) => {
      const contact = store.contacts?.find((c) => c.id === item.contact_type_id)
      return {
        ...cloneDeep(item),
        code: contact?.code || '',
        value: item.value || '',
      }
    }),
    name: currentUser.value?.info.name ?? '',
    surname: currentUser.value?.info.surname ?? '',
    middlename: currentUser.value?.info.middlename ?? '',
    phone: currentUser.value?.info.phone ?? '',
    file: currentUser.value?.info.file ?? null,
    email: currentUser.value?.info.email ?? undefined,
  })

  let initialFormData = createInitialData()
  const ContactForm = ref<IUpdateUserData>(cloneDeep(initialFormData))

  function normalizeContactData(data: IUpdateUserData) {
    return {
      ...data,
      phone: data.phone?.replace(/\D/g, '') || '',
      contacts: data.contacts.map((contact) => ({
        ...contact,
        value: ['phone', 'whatsapp'].includes(contact.code)
          ? contact.value?.replace(/\D/g, '') || ''
          : contact.value || '',
      })),
    }
  }

  const checkFormChanges = useDebounceFn(() => {
    const normalizedNew = normalizeContactData(ContactForm.value)
    const normalizedInitial = normalizeContactData(initialFormData)
    const isChanged = !isEqual(normalizedNew, normalizedInitial)
    emit('changed', isChanged)
  }, 100)

  watch(
    ContactForm,
    () => {
      initialFormData = createInitialData()
      checkFormChanges()
    },
    { deep: true }
  )

  const items = computed(() =>
    (store.contacts || []).map((item) => ({
      label: item.title,
      value: item.code,
      id: item.id,
    }))
  )

  async function onSubmit() {
    emit('loading', true)

    const dataToSend = JSON.parse(
      JSON.stringify(normalizeContactData(ContactForm.value))
    )
    dataToSend.file = ContactForm.value?.file?.id || null
    const res = await store.updateUser(dataToSend)

    emit('loading', false)

    if (res) {
      toast.add({
        description: t('pages.me.update-user-data.toast.success'),
        color: 'primary',
      })
      emit('changed', false)
    } else {
      toast.add({
        description: t('pages.me.update-user-data.toast.error'),
        color: 'error',
      })
    }
  }

  defineExpose({
    onSave: async () => {
      try {
        const validation = await formRef.value?.validate()
        if (!validation?.valid) {
          throw validation?.error
        }

        if (
          ContactForm.value.file &&
          ContactForm.value.file.length > 0 &&
          !ContactForm.value.file?.id
        ) {
          const res = await fileStore.sendFiles(ContactForm.value.file)

          if (res?.length) {
            ContactForm.value.file = res[res.length - 1]
          }
        }

        await onSubmit()

        return true
      } catch (error) {
        console.error('Form validation failed:', error)
        return false
      }
    },
  })
</script>

<template>
  <div class="flex w-full max-w-96 flex-col items-center justify-center gap-4">
    <h2 class="pb-1 text-base">
      {{
        isEditing
          ? t('pages.me.update-user-data.title')
          : t('pages.me.contact-information.title')
      }}
    </h2>

    <TransitionContainer>
      <UpdateUserDataView
        v-if="!isEditing"
        :current-user="currentUser"
        :items="items"
      />

      <UpdateUserDataForm
        v-else
        ref="formRef"
        v-model="ContactForm"
        :items="items"
        :is-editing="isEditing"
      />
    </TransitionContainer>
  </div>
</template>
